import { useState, useEffect, useMemo, useCallback } from 'react';
import { useFiscalYear } from '../context/FiscalYearContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL } from '../utils/apiConfig';
import { Lock, Unlock, Copy, Trash2, Pencil, RotateCcw } from 'lucide-react';

const MIN_YEAR = 2020;
const YEARS_AHEAD = 10;

export const useFiscalYearsPage = () => {
  const { t, language } = useLanguage();
  const { showError } = useToast();
  const {
    fiscalYears, currentFiscalYear, selectFiscalYear,
    createFiscalYear, closeFiscalYear, rectifyFiscalYear,
    duplicateToNewYear, refreshFiscalYears,
    // ENH-05 lookup live scenarios partagé + refetch après mutations
    scenariosById, refetchScenarios,
  } = useFiscalYear();
  // Sprint 4 BUG-07 complément FE : permissions admin/cs_admin pour Réouvrir
  // (cohérent backend require_fy_modify_state, fiscal_years.py:797).
  const { isCsAdmin, isCompanyAdmin } = useAuth();
  const isAdmin = isCsAdmin || isCompanyAdmin;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showRectifyModal, setShowRectifyModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameScenarioModal, setShowRenameScenarioModal] = useState(false);
  const [renameScenarioName, setRenameScenarioName] = useState('');
  const [renameScenarioError, setRenameScenarioError] = useState('');
  const [selectedFY, setSelectedFY] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // P2 LOT B — pré-clôture : diagnostic de plausibilité affiché (bloquant-soft)
  // dans la CloseModal. Le check tourne à l'OUVERTURE du flux (réutilise
  // POST /api/plausibility/check, pas de nouvel endpoint). Résultat inerte tant
  // que la modale est fermée ; la clôture reste TOUJOURS possible.
  // LOT D — la locale ACTIVE de l'UI est transmise au BE (qui traduit les
  // messages) : LanguageContext vit dans localStorage et n'est jamais
  // synchronisé vers `user.language`. `language` en dépendance = re-fetch au
  // basculement de langue si la modale est ouverte (sinon messages figés).
  const [closePlausibility, setClosePlausibility] = useState(null);
  const [closePlausibilityLoading, setClosePlausibilityLoading] = useState(false);
  useEffect(() => {
    if (!showCloseModal || !selectedFY?.id) { setClosePlausibility(null); return; }
    let cancelled = false;
    setClosePlausibilityLoading(true);
    setClosePlausibility(null);
    const params = new URLSearchParams({ fiscal_year_id: selectedFY.id, language });
    axios.post(`${API_URL}/api/plausibility/check?${params.toString()}`)
      .then(res => { if (!cancelled) setClosePlausibility(res.data); })
      .catch(err => { if (!cancelled) logger.error('Pre-close plausibility check failed:', err); })
      .finally(() => { if (!cancelled) setClosePlausibilityLoading(false); });
    return () => { cancelled = true; };
  }, [showCloseModal, selectedFY?.id, language]);

  const [createForm, setCreateForm] = useState({
    year: new Date().getFullYear() + 1, duplicateFrom: null,
    duplicateActivities: false, duplicateProducts: true, isScenario: false,
    selectedScenarioId: '', newScenarioName: '',
  });
  const [createError, setCreateError] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [rectifyReason, setRectifyReason] = useState('');
  // Sprint 4 BUG-07 FE 422 fix : alignement schéma backend FiscalYearReopen
  // — reason_type (enum 4 options) + reason_text (Optional, obligatoire si
  // reason_type='autre'). Remplace l'ancien champ reason simple.
  const [reopenReasonType, setReopenReasonType] = useState('');
  const [reopenReasonText, setReopenReasonText] = useState('');
  const [reopenError, setReopenError] = useState('');
  const [deleteStats, setDeleteStats] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const fetchScenarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/scenarios`);
      setScenarios(res.data || []);
    } catch (e) {
      logger.error('Failed to fetch scenarios:', e);
    }
  }, []);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  const existingYears = useMemo(
    () => new Set(fiscalYears.filter(fy => fy.type !== 'scenario').map(fy => fy.year || parseInt(fy.name?.replace('Exercice ', '')))),
    [fiscalYears],
  );

  const availableYears = useMemo(() => {
    const maxYear = new Date().getFullYear() + YEARS_AHEAD;
    const years = [];
    for (let y = MIN_YEAR; y <= maxYear; y++) years.push({ year: y, available: !existingYears.has(y) });
    return years;
  }, [existingYears]);

  const scenarioTakenYears = useMemo(() => {
    if (!createForm.isScenario || !createForm.selectedScenarioId || createForm.selectedScenarioId === 'new') return new Set();
    return new Set(fiscalYears.filter(fy => fy.type === 'scenario' && fy.scenario_id === createForm.selectedScenarioId).map(fy => fy.year));
  }, [fiscalYears, createForm.isScenario, createForm.selectedScenarioId]);

  useEffect(() => {
    if (createForm.isScenario && createForm.selectedScenarioId && createForm.selectedScenarioId !== 'new') {
      if (scenarioTakenYears.has(createForm.year)) {
        const firstFree = availableYears.find(y => !scenarioTakenYears.has(y.year));
        if (firstFree) setCreateForm(prev => ({ ...prev, year: firstFree.year }));
      }
    }
  }, [createForm.selectedScenarioId, scenarioTakenYears, createForm.isScenario, createForm.year, availableYears]);

  useEffect(() => {
    const first = availableYears.find(y => y.available);
    if (first && showCreateModal) setCreateForm(prev => ({ ...prev, year: first.year }));
  }, [availableYears, showCreateModal]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const locale = language === 'de' ? 'de-DE' : 'fr-FR';
    return new Date(dateStr).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getActivityCount = (fy) => fy.activities_count || 0;

  const handleCreate = async () => {
    if (!createForm.year) return;
    if (createForm.isScenario) {
      if (createForm.selectedScenarioId === '') { setCreateError(t('fiscalYears.errors.selectOrCreateScenario')); return; }
      if (createForm.selectedScenarioId === 'new' && !createForm.newScenarioName.trim()) { setCreateError(t('fiscalYears.errors.scenarioNameRequired')); return; }
    }
    if (!createForm.isScenario && existingYears.has(createForm.year)) { setCreateError(t('fiscalYears.errors.yearAlreadyExists').replace('{year}', createForm.year)); return; }

    setLoading(true);
    setCreateError('');
    try {
      if (createForm.duplicateFrom) {
        let scenarioId = null;
        if (createForm.isScenario) {
          if (createForm.selectedScenarioId === 'new') {
            const res = await axios.post(`${API_URL}/api/scenarios`, { name: createForm.newScenarioName.trim() });
            scenarioId = res.data.id;
            await fetchScenarios();
            // ENH-05 : sync Map context partagé pour Selector/Card immédiat
            refetchScenarios();
          } else { scenarioId = createForm.selectedScenarioId; }
        }
        await duplicateToNewYear(createForm.duplicateFrom, {
          new_year: createForm.year, duplicate_activities: createForm.duplicateActivities,
          duplicate_products: createForm.duplicateProducts ?? true,
          activity_ids_to_duplicate: [], is_scenario: createForm.isScenario,
          scenario_id: scenarioId || undefined,
        });
      } else if (!createForm.isScenario) {
        await createFiscalYear({ year: createForm.year });
      } else {
        setCreateError(t('fiscalYears.errors.scenarioNeedBase'));
        setLoading(false);
        return;
      }
      setShowCreateModal(false);
      setCreateForm({ year: new Date().getFullYear() + 1, duplicateFrom: null, duplicateActivities: false, duplicateProducts: true, isScenario: false, selectedScenarioId: '', newScenarioName: '' });
    } catch (error) {
      logger.error('Failed to create fiscal year:', error);
      setCreateError(error.response?.data?.detail || t('fiscalYears.errors.createError'));
    } finally { setLoading(false); }
  };

  const handleClose = async () => {
    if (!selectedFY) return;
    setLoading(true);
    try { await closeFiscalYear(selectedFY.id); setShowCloseModal(false); setSelectedFY(null); }
    catch (error) { logger.error('Failed to close fiscal year:', error); showError(error.response?.data?.detail || t('fiscalYears.errors.closeError')); }
    finally { setLoading(false); }
  };

  const handleRectify = async () => {
    if (!selectedFY || !rectifyReason) return;
    setLoading(true);
    try { await rectifyFiscalYear(selectedFY.id, rectifyReason); setShowRectifyModal(false); setSelectedFY(null); setRectifyReason(''); }
    catch (error) { logger.error('Failed to rectify:', error); showError(error.response?.data?.detail || t('fiscalYears.errors.rectifyError')); }
    finally { setLoading(false); }
  };

  // Sprint 4 BUG-07 FE 422 fix — handleReopen : POST /api/fiscal-years/{id}/reopen
  // Payload aligné sur backend FiscalYearReopen :
  //   { reason_type: Literal[bug_system|erreur_saisie|maj_facteur|autre],
  //     reason_text: Optional[str] }
  // Validation client : reason_type requis ; reason_text obligatoire si
  // reason_type='autre' (≥ 5 chars, BE re-valide). Catch 422 Pydantic detail[]
  // proprement → toujours une string dans setReopenError (résout React crash #31
  // observé en smoke staging quand detail = array d'objets).
  const handleReopen = async () => {
    if (!selectedFY) return;
    if (!reopenReasonType) {
      setReopenError(t('fiscalYears.reopenModal.errorReasonTypeMissing'));
      return;
    }
    const reasonText = reopenReasonText.trim();
    if (reopenReasonType === 'autre' && reasonText.length < 5) {
      setReopenError(t('fiscalYears.reopenModal.errorReasonTextRequired'));
      return;
    }
    setLoading(true);
    setReopenError('');
    try {
      await axios.post(`${API_URL}/api/fiscal-years/${selectedFY.id}/reopen`, {
        reason_type: reopenReasonType,
        reason_text: reasonText || null,
      });
      await refreshFiscalYears();
      setShowReopenModal(false);
      setSelectedFY(null);
      setReopenReasonType('');
      setReopenReasonText('');
    } catch (error) {
      logger.error('Failed to reopen:', error);
      const detail = error.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        // Pydantic 422 : array of {type, loc, msg, input} → string lisible
        msg = detail
          .map(d => `${(d.loc || []).join('.')}: ${d.msg}`)
          .join(' ; ');
      } else if (typeof detail === 'string') {
        msg = detail;
      } else {
        msg = t('fiscalYears.errors.reopenError');
      }
      setReopenError(msg);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = async (fy) => {
    setSelectedFY(fy); setDeleteConfirmText(''); setDeleteError(''); setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/summary?fiscal_year_id=${fy.id}`);
      setDeleteStats({ activitiesCount: response.data?.activities_count || 0, totalEmissions: response.data?.total_emissions || 0 });
    } catch { setDeleteStats({ activitiesCount: 0, totalEmissions: 0 }); }
    finally { setLoading(false); setShowDeleteModal(true); }
  };

  const handleDelete = async () => {
    if (!selectedFY || deleteConfirmText !== selectedFY.name) return;
    setLoading(true); setDeleteError('');
    try {
      await axios.delete(`${API_URL}/api/fiscal-years/${selectedFY.id}`);
      await refreshFiscalYears();
      setShowDeleteModal(false); setSelectedFY(null); setDeleteStats(null); setDeleteConfirmText('');
    } catch (error) {
      logger.error('Failed to delete:', error);
      setDeleteError(error.response?.data?.detail || t('fiscalYears.errors.deleteError'));
    } finally { setLoading(false); }
  };

  const getAvailableActions = (fy) => {
    const actions = [];
    if (fy.status === 'draft') actions.push({ key: 'close', label: t('fiscalYears.actions.close'), icon: Lock, color: 'green' });
    if (fy.status === 'closed') actions.push({ key: 'rectify', label: t('fiscalYears.actions.rectify'), icon: Unlock, color: 'orange' });
    if (fy.status === 'rectified') actions.push({ key: 'close', label: t('fiscalYears.actions.reclose'), icon: Lock, color: 'green' });
    // Sprint 4 BUG-07 complément FE : Réouvrir visible sur closed/rectified pour admin/cs_admin.
    // Cohérent backend require_fy_modify_state (fiscal_years.py:797 + reopen l.589).
    if ((fy.status === 'closed' || fy.status === 'rectified') && isAdmin) {
      actions.push({ key: 'reopen', label: t('fiscalYears.actions.reopen'), icon: RotateCcw, color: 'blue' });
    }
    // Campagne #3 A2 : duplication FY = require_fy_modify_state (cs_admin OU admin).
    if (isAdmin) {
      actions.push({ key: 'duplicate', label: t('fiscalYears.actions.duplicateTo'), icon: Copy, color: 'blue' });
    }
    // Campagne #3 A3 : renommage scenario = require_company_admin (role=admin strict).
    if (fy.type === 'scenario' && fy.scenario_id && isCompanyAdmin) {
      actions.push({ key: 'rename-scenario', label: t('fiscalYears.actions.renameScenario'), icon: Pencil, color: 'violet' });
    }
    // Sprint 4 BUG-07 complément FE : Supprimer disabled sur closed/rectified
    // (backend BUG-07 retourne 403, sortir le bouton du flow utilisateur cassé).
    const deleteDisabled = fy.status === 'closed' || fy.status === 'rectified';
    actions.push({
      key: 'delete',
      label: t('fiscalYears.actions.delete'),
      icon: Trash2,
      color: 'red',
      disabled: deleteDisabled,
      disabledTooltip: deleteDisabled ? t('fiscalYears.actions.deleteDisabledTooltip') : undefined,
    });
    return actions;
  };

  const handleRenameScenario = async () => {
    if (!selectedFY || !selectedFY.scenario_id) return;
    const name = renameScenarioName.trim();
    if (!name) { setRenameScenarioError(t('fiscalYears.errors.scenarioNameRequired')); return; }
    setLoading(true); setRenameScenarioError('');
    try {
      await axios.put(`${API_URL}/api/scenarios/${selectedFY.scenario_id}`, { name });
      await refreshFiscalYears();
      await fetchScenarios();
      // ENH-05 : sync Map context partagé → Selector/Card reflètent le rename immédiat (sinon stale name)
      refetchScenarios();
      setShowRenameScenarioModal(false);
      setSelectedFY(null);
      setRenameScenarioName('');
    } catch (error) {
      logger.error('Failed to rename scenario:', error);
      setRenameScenarioError(error.response?.data?.detail || t('fiscalYears.errors.renameScenarioError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (fy, action) => {
    setOpenMenuId(null); setSelectedFY(fy);
    switch (action) {
      case 'close': setShowCloseModal(true); break;
      case 'rectify': setShowRectifyModal(true); break;
      case 'reopen': setReopenReasonType(''); setReopenReasonText(''); setReopenError(''); setShowReopenModal(true); break;
      case 'duplicate': {
        const nextYear = (fy.year || parseInt(fy.name?.replace('Exercice ', ''))) + 1;
        const targetYear = availableYears.find(y => y.year >= nextYear && y.available)?.year || nextYear;
        setCreateForm({ year: targetYear, duplicateFrom: fy.id, duplicateActivities: true, duplicateProducts: true, isScenario: false, scenarioName: '' });
        setShowCreateModal(true);
        break;
      }
      case 'rename-scenario':
        // ENH-05 lookup live : pré-remplit modal avec nom courant de l'entité scenario (pas le dénormalisé déprécié)
        setRenameScenarioName(scenariosById.get(fy.scenario_id)?.name ?? '');
        setRenameScenarioError('');
        setShowRenameScenarioModal(true);
        break;
      case 'delete': openDeleteModal(fy); break;
      default: break;
    }
  };

  const sortedFiscalYears = useMemo(() => {
    return [...fiscalYears].sort((a, b) => {
      const yearA = a.year || parseInt(a.name?.replace('Exercice ', ''));
      const yearB = b.year || parseInt(b.name?.replace('Exercice ', ''));
      if (yearA !== yearB) return yearA - yearB;
      return (a.type === 'scenario' ? 1 : 0) - (b.type === 'scenario' ? 1 : 0);
    });
  }, [fiscalYears]);

  return {
    t, fiscalYears, currentFiscalYear, selectFiscalYear,
    showCreateModal, setShowCreateModal, showCloseModal, setShowCloseModal,
    closePlausibility, closePlausibilityLoading,
    showRectifyModal, setShowRectifyModal, showDeleteModal, setShowDeleteModal,
    showReopenModal, setShowReopenModal,
    showRenameScenarioModal, setShowRenameScenarioModal,
    renameScenarioName, setRenameScenarioName, renameScenarioError,
    selectedFY, loading, openMenuId, setOpenMenuId,
    createForm, setCreateForm, createError, setCreateError, scenarios,
    rectifyReason, setRectifyReason,
    reopenReasonType, setReopenReasonType,
    reopenReasonText, setReopenReasonText,
    reopenError,
    deleteStats, deleteConfirmText,
    setDeleteConfirmText, deleteError,
    existingYears, availableYears, scenarioTakenYears,
    isAdmin, isCompanyAdmin,
    formatDate, getActivityCount, getAvailableActions,
    handleCreate, handleClose, handleRectify, handleReopen, handleDelete, handleAction,
    handleRenameScenario,
    sortedFiscalYears,
  };
};
